import React, {FC, LegacyRef, useEffect, useCallback} from "react"
import {formatTextTestIds} from "@next/utils"
import {breakpoints} from "@next/themes"
import {useMediaQuery} from "../../../../../../hooks/useMediaQuery"
import env from "../../../../../../config/env"
import {ThumbsContainer, ThumbImage} from "./components"
import getActiveBreakpoint from "../../../../../../utils/getActiveBreakpoint"
import {MobileThumbnailConfig} from "../../../../../../models/ProductSummary"
import {LAZY_SIZES_CLASSNAME} from "../../../../../../config/constants"
import {AdditionalColourchipsLoadingState} from "../../../../../../models/AdditionalColourchipsLoadingState"
import {EmblaContainer, EmblaSlide, EmblaViewport} from "../../../../components"

interface ColourChip {
    itemNumber: string
    colourChipUrl: string
    linkUrl: string
    isSelected?: boolean
    colour?: string | null
    title: string
    price: string
    currencyCode: string
    altText: string
    department: string
}

interface ThumbsSwiperCarouselProps {
    colourchips: ColourChip[]
    lazyloadColourchips?: boolean
    mobileThumbnailConfig: MobileThumbnailConfig
    textAlignment: string
    fetchAdditionalColourchipsThunk: () => void
    numberOfAllColourways: number
    setAdditionalColourchipsLoading: (boolean) => void
    additionalColourchipsLoading: AdditionalColourchipsLoadingState
    thumbsEmblaRef: LegacyRef<HTMLDivElement> | undefined
    thumbsSlideIndex: (index: number) => void
    slideIndex: (index: number) => void
}

export const StandardThumbsSwiperCarousel: FC<ThumbsSwiperCarouselProps> = ({
    colourchips,
    lazyloadColourchips,
    mobileThumbnailConfig,
    textAlignment,
    fetchAdditionalColourchipsThunk,
    numberOfAllColourways,
    setAdditionalColourchipsLoading,
    additionalColourchipsLoading,
    thumbsEmblaRef,
    thumbsSlideIndex,
    slideIndex,
}) => {
    const hasMoreColours = numberOfAllColourways > colourchips.length
    const isMobileBreakpoint = useMediaQuery(`(max-width:${breakpoints.values.md}px)`)

    useEffect(() => {
        if (hasMoreColours && isMobileBreakpoint) {
            // window.requestIdleCallback(() => {
            if (additionalColourchipsLoading === AdditionalColourchipsLoadingState.Default) {
                fetchAdditionalColourchipsThunk()
            }
            if (additionalColourchipsLoading === AdditionalColourchipsLoadingState.Loaded) {
                setAdditionalColourchipsLoading(AdditionalColourchipsLoadingState.Default)
            }
            // })
        }
    }, [hasMoreColours, isMobileBreakpoint])

    const breakpointsConfig = [
        {
            size: "xs",
            isActive: true, // screen size is always > 0
            slidesPerView: mobileThumbnailConfig.xs,
        },
        {
            size: "sm",
            isActive: useMediaQuery(`(min-width:${breakpoints.values.sm}px)`),
            slidesPerView: mobileThumbnailConfig.sm,
        },
        {
            size: "md",
            isActive: useMediaQuery(`(min-width:${breakpoints.values.md}px)`),
            slidesPerView: mobileThumbnailConfig.md,
        },
        {
            size: "lg",
            isActive: useMediaQuery(`(min-width:${breakpoints.values.lg}px)`),
            // large & xl don't use `slidesPerView`
        },
    ]

    const activeBreakpoint = getActiveBreakpoint(breakpointsConfig)

    const onClickThumb = useCallback(
        (itemNumber: string) => {
            const index = colourchips.findIndex(chip => chip.itemNumber === itemNumber)
            thumbsSlideIndex(index)
            slideIndex(index)
        },
        [colourchips, slideIndex, thumbsSlideIndex],
    )

    if (!colourchips || colourchips.length <= 1) return null

    return (
        <ThumbsContainer data-testid={formatTextTestIds("product_summary_thumbs_gallery")} tabIndex={0}>
            <EmblaViewport className="swiper-container" ref={thumbsEmblaRef} dir={textAlignment}>
                <EmblaContainer className="swiper-wrapper">
                    {colourchips.map(chip => (
                        <EmblaSlide
                            $slidesPerView={activeBreakpoint.slidesPerView}
                            data-testid="carousel-slide"
                            aria-current={chip.isSelected ? "true" : "false"}
                            key={chip.itemNumber}
                            className={`swiper-slide mobile ${chip.isSelected ? "swiper-slide-thumb-active" : ""}`}
                        >
                            <ThumbImage
                                onClick={() => onClickThumb(chip.itemNumber)}
                                src={
                                    lazyloadColourchips
                                        ? `${env.REACT_APP_BLOB_STORAGE_PATH}/images/ps/ph.jpg`
                                        : chip.colourChipUrl
                                }
                                data-src={chip.colourChipUrl}
                                className={lazyloadColourchips ? LAZY_SIZES_CLASSNAME : ""}
                                alt={chip.altText}
                                data-testid={formatTextTestIds(`product_summary_thumb_colourchip_${chip.itemNumber}`)}
                            />
                        </EmblaSlide>
                    ))}
                </EmblaContainer>
            </EmblaViewport>
        </ThumbsContainer>
    )
}

export default StandardThumbsSwiperCarousel
